import axios from '@/axiosInterceptor';
import { v4 as uuidv4 } from 'uuid';

const state = () => ({
  carrierIdPersisted: null,
  directory: [],
  contacts: [],
  users: [],
  dispatchUser: [],
  fmcsa: [],
  //Bool properties:
  userLoader: false,
  contactLoader: false,
  dispatchUsersLoader: false,
  fmcsaLoader: false,
  //Scrollers:
  expandUser: null,
  scrollTo: false,
  userPagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  directoryUsersLoading: false,
});

// getters
const getters = {};

// actions
const actions = {
  async getDirectory({ commit }, carrierId) {
    try {
      const response = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrierId}/directory`
      );

      // Remove duplicates before updating state:
      let directoryData = response?.data;
      if (directoryData?.users?.length > 0) {
        const formattedUsers = optimizedRemoveDuplicates(directoryData.users);
        directoryData.users = formattedUsers;

        if (directoryData?.total_users >= 50) {
          commit('setUserPagination', {
            totalRecords: directoryData?.total_users,
            totalPages: Math.ceil(directoryData?.total_users / 50),
            currentPage: 1,
          });
        }
      }

      if (directoryData?.contacts?.length > 0)
        directoryData.contacts = optimizedRemoveDuplicates(
          directoryData.contacts
        );

      const fmcsaInfo = await directoryData['fmcsa_information'];
      const fmcsaFormatted = formatFmcsaInfo(fmcsaInfo);
      directoryData['fmcsa_information'] = fmcsaFormatted;

      commit('setDirectory', directoryData);
      commit('setCarrierIdPersisted', carrierId);
      return response;
    } catch (error) {
      console.error('Error fetching directory:', error.message);
      throw error; // Propagate the error up
    }
  },
  async getDirectoryUsers({ commit, state }, { carrierId, currentPage }) {
    try {
      commit('setDirectoryUsersLoading', true);
      const per_page = 50;
      const response = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/${carrierId}/directory_users?page=${currentPage}&per_page=${per_page}`
      );

      if (response?.headers) {
        commit('setUserPagination', {
          totalRecords: Number(response?.headers?.totalitems),
          totalPages: Number(response?.headers?.totalpages),
          currentPage: Number(response?.headers?.currentpage),
        });
      }

      const users = optimizedRemoveDuplicates(response?.data?.users);

      let directoryData = { ...state.directory };
      directoryData['users'] = users;

      commit('setDirectory', directoryData);
      return response;
    } catch (error) {
      console.error('Error fetching directory:', error.message);
      throw error; // Propagate the error up
    } finally {
      commit('setDirectoryUsersLoading', false);
    }
  },
  async getContactByUrl({ commit }, link) {
    try {
      commit('setContactLoader', true);
      const response = await axios.get(`${process.env.API_MONITOR}${link}`);
      commit('setContactLoader', false);
      commit('setContact', response.data);
      return response;
    } catch (error) {
      return error;
    }
  },
  async getUserByUrl({ commit }, { link, userId }) {
    try {
      commit('setUserLoader', true);
      const response = await axios.get(`${process.env.API_MONITOR}${link}`);
      commit('setUserLoader', false);
      commit('setUser', { data: response.data, userId: userId });
      return response;
    } catch (error) {
      return error;
    }
  },
  async getDispatchUserByUrl({ commit }, { link, userId }) {
    try {
      commit('setDispatchUserLoader', true);
      const response = await axios.get(`${process.env.API_MONITOR}${link}`);
      commit('setDispatchUserLoader', false);
      commit('setDispatchUser', { data: response.data, userId: userId });
      return response;
    } catch (error) {
      return error;
    }
  },
  async getFMCSAInfoByUrl({ commit }, { link, id }) {
    try {
      commit('setFMCSALoader', true);
      const response = await axios.get(`${process.env.API_MONITOR}${link}`);
      commit('setFMCSALoader', false);
      commit('setFMCSA', { data: response.data, uuid: id });
      return response;
    } catch (error) {
      return error;
    }
  },
};

// mutations
const mutations = {
  setScrollToUsers(state, data) {
    state.scrollTo = data;
  },
  setDirectory(state, directory) {
    state.directory = directory;
  },
  setContact(state, contact) {
    const contacts = [...state.contacts, contact];
    state.contacts = contacts;
  },
  setUser(state, { data, userId }) {
    data.id = userId;
    const users = [...state.users, data];
    state.users = users;
  },
  setUserLoader(state, loader) {
    state.userLoader = loader;
  },
  setContactLoader(state, loader) {
    state.contactLoader = loader;
  },
  setDispatchUserLoader(state, loader) {
    state.dispatcherLoader = loader;
  },
  setDispatchUser(state, { data, userId }) {
    data.id = userId;
    const users = [...state.users, data];
    state.dispatchUser = users;
  },
  setExpandUser(state, userElement) {
    state.expandUser = userElement;
  },
  setFMCSA(state, { data, uuid }) {
    data.uuid = uuid;
    const fmcsas = [...state.fmcsa, data];
    state.fmcsa = fmcsas;
  },
  setFMCSALoader(state, loader) {
    state.fmcsaLoader = loader;
  },
  setCarrierIdPersisted(state, carrierId) {
    state.carrierIdPersisted = carrierId;
  },
  setUserPagination(state, data) {
    state.userPagination = data;
  },
  setDirectoryUsersLoading(state, boolean) {
    state.directoryUsersLoading = boolean;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

function formatFmcsaInfo(data) {
  const assignId = (obj) => {
    obj.id = uuidv4();
    return obj;
  };

  const formattedData = { ...data };

  formattedData.names = data?.names?.map(assignId) || data?.names;
  formattedData.phone_numbers =
    data?.phone_numbers?.map(assignId) || data?.phone_numbers;
  formattedData.email_addresses =
    data?.email_addresses?.map(assignId) || data?.email_addresses;
  formattedData.addresses = data?.addresses?.map(assignId) || data?.addresses;

  return formattedData;
}

function optimizedRemoveDuplicates(dataArray) {
  const mergedData = {};
  dataArray.forEach((item) => {
    const key = `${item.name}-${item.phone?.value}-${item.email_address?.value}`;

    if (mergedData[key]) {
      item.is_types?.forEach((type) => {
        if (!mergedData[key].is_types?.includes(type)) {
          mergedData[key].is_types.push(type);
        }
      });
    } else {
      mergedData[key] = item;
    }
  });

  return Object.values(mergedData);
}
